import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { parseDate } from '@/lib/localizedDateUtils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
  Phone, 
  MessageCircle, 
  ClipboardList,
  History,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { OverduePayment } from '@/hooks/finance/useCollectionDashboard';

export interface RenegotiationDealData {
  dealId: string;
  dealName: string;
  contactId: string;
  clientName: string;
  totalOverdue: number;
  paymentCount: number;
  currency: string;
}

interface GroupedDeal {
  dealId: string;
  dealName: string;
  contactId: string;
  clientName: string;
  phone: string | null;
  phoneCountryCode: string | null;
  payments: OverduePayment[];
  totalOverdue: number;
  paymentCount: number;
  maxDaysOverdue: number;
  currency: string;
}

interface ClientIndicators {
  hasApprovedRenegotiation?: boolean;
  activePromise?: { date: string } | null;
  brokenPromiseCount?: number;
}

interface GroupedPaymentsTableProps {
  payments: OverduePayment[];
  indicators?: Record<string, ClientIndicators>;
  onOpenTimeline: (contact: { id: string; name: string }) => void;
  onRegisterContact: (payment: OverduePayment) => void;
  onRequestRenegotiation: (deal: RenegotiationDealData) => void;
}

function formatCurrency(value: number, currency: string = 'USD') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatPhone(phone: string | null, countryCode: string | null): string {
  if (!phone) return '-';
  const code = countryCode || '';
  return `${code}${phone}`.replace(/\s+/g, '');
}

function getWhatsAppUrl(phone: string | null, countryCode: string | null, clientName: string): string {
  const fullPhone = formatPhone(phone, countryCode).replace(/[^0-9]/g, '');
  if (!fullPhone) return '#';
  const message = encodeURIComponent(`Olá ${clientName}, tudo bem? Estou entrando em contato sobre um pagamento pendente.`);
  return `https://wa.me/${fullPhone}?text=${message}`;
}

function AgingBadge({ dias }: { dias: number }) {
  let variant: 'default' | 'secondary' | 'destructive' | 'outline' = 'secondary';
  let label = '';
  
  if (dias <= 30) {
    variant = 'secondary';
    label = '1-30d';
  } else if (dias <= 60) {
    variant = 'default';
    label = '31-60d';
  } else if (dias <= 90) {
    variant = 'destructive';
    label = '61-90d';
  } else {
    variant = 'destructive';
    label = '90+d';
  }
  
  return (
    <Badge variant={variant} className="font-mono">
      {dias}d ({label})
    </Badge>
  );
}

export function GroupedPaymentsTable({
  payments,
  indicators,
  onOpenTimeline,
  onRegisterContact,
  onRequestRenegotiation,
}: GroupedPaymentsTableProps) {
  const [expandedDeals, setExpandedDeals] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'amount' | 'days'>('amount');

  // Agrupa pagamentos por deal (não por cliente)
  const groupedDeals = useMemo(() => {
    const groups = new Map<string, GroupedDeal>();

    payments.forEach(payment => {
      const existing = groups.get(payment.deal_id);
      
      // Extrai nome do deal da descrição (ex: "EB2-NIW - Parcela 1" -> "EB2-NIW")
      const dealName = payment.description?.split(' - ')?.[0] || 'Contrato';
      
      if (existing) {
        existing.payments.push(payment);
        existing.totalOverdue += payment.valor_aberto || 0;
        existing.paymentCount++;
        existing.maxDaysOverdue = Math.max(existing.maxDaysOverdue, payment.dias_atraso);
      } else {
        groups.set(payment.deal_id, {
          dealId: payment.deal_id,
          dealName,
          contactId: payment.contact_id,
          clientName: payment.cliente,
          phone: payment.primary_phone,
          phoneCountryCode: payment.primary_phone_country_code,
          payments: [payment],
          totalOverdue: payment.valor_aberto || 0,
          paymentCount: 1,
          maxDaysOverdue: payment.dias_atraso,
          currency: payment.currency,
        });
      }
    });

    // Converte para array e ordena
    const sorted = Array.from(groups.values()).sort((a, b) => {
      if (sortBy === 'amount') {
        return b.totalOverdue - a.totalOverdue;
      }
      return b.maxDaysOverdue - a.maxDaysOverdue;
    });

    return sorted;
  }, [payments, sortBy]);

  const toggleExpanded = (dealId: string) => {
    const newExpanded = new Set(expandedDeals);
    if (newExpanded.has(dealId)) {
      newExpanded.delete(dealId);
    } else {
      newExpanded.add(dealId);
    }
    setExpandedDeals(newExpanded);
  };

  const uniqueDeals = groupedDeals.length;
  const uniqueClients = new Set(groupedDeals.map(d => d.contactId)).size;
  const totalPayments = payments.length;

  return (
    <div className="space-y-4">
      {/* Sort Controls */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">
          {uniqueClients} clientes • {totalPayments} parcelas
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm font-medium">Ordenar por:</span>
          <Button
            variant={sortBy === 'amount' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('amount')}
          >
            Maior Valor
          </Button>
          <Button
            variant={sortBy === 'days' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSortBy('days')}
          >
            Maior Atraso
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Indicadores</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Parcelas</TableHead>
              <TableHead>Total em Atraso</TableHead>
              <TableHead>Maior Atraso</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedDeals.map((deal) => {
              const isExpanded = expandedDeals.has(deal.dealId);
              const clientIndicators = indicators?.[deal.contactId];
              const firstPayment = deal.payments[0];

              return (
                <Collapsible key={deal.dealId} asChild open={isExpanded}>
                  <>
                    {/* Main Row */}
                    <TableRow className="hover:bg-muted/50">
                      <TableCell>
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => toggleExpanded(deal.dealId)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <button 
                            onClick={() => onOpenTimeline({ 
                              id: deal.contactId, 
                              name: deal.clientName 
                            })}
                            className="font-medium hover:underline flex items-center gap-1 text-left"
                          >
                            {deal.clientName}
                            <History className="h-3 w-3 text-muted-foreground" />
                          </button>
                          <span className="text-xs text-muted-foreground">
                            {deal.dealName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <div className="flex items-center gap-1">
                            {clientIndicators?.hasApprovedRenegotiation && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                                    🔄
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Cliente já teve renegociação aprovada</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {clientIndicators?.activePromise && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                    📅 {safeFormat(new Date(clientIndicators.activePromise.date), 'dd/MM')}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Promessa de pagamento em {safeFormat(new Date(clientIndicators.activePromise.date), 'dd/MM/yyyy', { locale: ptBR })}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {clientIndicators?.brokenPromiseCount && clientIndicators.brokenPromiseCount > 0 && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                                    ⚠️ {clientIndicators.brokenPromiseCount}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Quebrou {clientIndicators.brokenPromiseCount} promessa(s) anteriormente</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {!clientIndicators?.hasApprovedRenegotiation && 
                             !clientIndicators?.activePromise && 
                             !clientIndicators?.brokenPromiseCount && (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </div>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">
                          {formatPhone(deal.phone, deal.phoneCountryCode)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{deal.paymentCount}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-destructive">
                          {formatCurrency(deal.totalOverdue, deal.currency)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <AgingBadge dias={deal.maxDaysOverdue} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            title="WhatsApp"
                          >
                            <a
                              href={getWhatsAppUrl(deal.phone, deal.phoneCountryCode, deal.clientName)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <MessageCircle className="h-4 w-4 text-green-600" />
                            </a>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            title="Ligar"
                          >
                            <a href={`tel:${formatPhone(deal.phone, deal.phoneCountryCode)}`}>
                              <Phone className="h-4 w-4 text-blue-600" />
                            </a>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Registrar Contato"
                            onClick={() => onRegisterContact(firstPayment)}
                          >
                            <ClipboardList className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Solicitar Renegociação"
                            onClick={() => onRequestRenegotiation({
                              dealId: deal.dealId,
                              dealName: deal.dealName,
                              contactId: deal.contactId,
                              clientName: deal.clientName,
                              totalOverdue: deal.totalOverdue,
                              paymentCount: deal.paymentCount,
                              currency: deal.currency,
                            })}
                          >
                            <RefreshCw className="h-4 w-4 text-orange-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Abrir Deal"
                            onClick={() => window.open(`/deals/${deal.dealId}`, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 text-indigo-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Rows */}
                    <CollapsibleContent asChild>
                      <>
                        {deal.payments.map((payment) => (
                          <TableRow 
                            key={payment.payment_id}
                            className="bg-muted/30 border-l-4 border-l-primary/20"
                          >
                            <TableCell></TableCell>
                            <TableCell colSpan={2}>
                              <div className="pl-4 flex items-center gap-2">
                                <span className="text-muted-foreground">└</span>
                                <span className="text-sm">{payment.description}</span>
                              </div>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">
                                {formatCurrency(payment.valor_aberto, payment.currency)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <span className="text-xs text-muted-foreground">
                                  {safeFormat(parseDate(payment.due_date)!, 'dd/MM/yyyy', { locale: ptBR })}
                                </span>
                                <Badge variant="outline" className="w-fit text-xs">
                                  {payment.dias_atraso}d
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="Registrar Contato"
                                  onClick={() => onRegisterContact(payment)}
                                >
                                  <ClipboardList className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    </CollapsibleContent>
                  </>
                </Collapsible>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
